import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

const ProfileContext = createContext({});

export const useProfile = () => useContext(ProfileContext);

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();

  // Current user's profile data from users table
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // For parents: which profile are they viewing as (null = their own, or kid's user_id)
  // Initialize from localStorage if available (will be validated after profile loads)
  const [activeProfileId, setActiveProfileId] = useState(() => {
    const stored = localStorage.getItem('activeProfileId');
    return stored || null;
  });

  // Kids linked to this user (if they're a parent)
  const [linkedKids, setLinkedKids] = useState([]);

  // Parents linked to this user (if they're a kid)
  const [linkedParents, setLinkedParents] = useState([]);

  // Fetch user profile and family links
  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setUserProfile(null);
      setLinkedKids([]);
      setLinkedParents([]);
      setLoading(false);
      return;
    }

    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
      }

      setUserProfile(profile || null);

      if (profile?.id) {
        // If user is a parent, fetch linked kids
        if (profile.is_parent) {
          const { data: kids } = await supabase
            .from('family_links')
            .select(`
              child_id,
              can_trade,
              users!family_links_child_id_fkey (
                id, first_name, last_name, username, avatar_url, hide_profile,
                latitude, longitude, location_city, location_state, is_under_18,
                pricing_trigger, pricing_enabled
              )
            `)
            .eq('parent_id', profile.id);

          setLinkedKids(kids || []);
        }

        // If user is under 18, fetch linked parents
        if (profile.is_under_18) {
          const { data: parents } = await supabase
            .from('family_links')
            .select(`
              parent_id,
              can_trade,
              users!family_links_parent_id_fkey (
                id, first_name, last_name, username
              )
            `)
            .eq('child_id', profile.id);

          setLinkedParents(parents || []);
        }
      }
    } catch (err) {
      console.error('Error in fetchProfile:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Validate and restore activeProfileId after profile and linkedKids load
  useEffect(() => {
    if (loading || !userProfile) return;

    // Only parents can have an activeProfileId (viewing as a child)
    if (!userProfile.is_parent) {
      // Not a parent - clear any stored activeProfileId
      if (activeProfileId) {
        setActiveProfileId(null);
        localStorage.removeItem('activeProfileId');
      }
      return;
    }

    // Parent - validate the stored activeProfileId
    const storedId = localStorage.getItem('activeProfileId');
    const dbDefaultId = userProfile.default_profile_id;

    // Determine which profile to use (localStorage takes priority, then DB)
    const targetProfileId = storedId || dbDefaultId;

    if (targetProfileId) {
      // Check if this is the parent's own ID (viewing as self)
      if (targetProfileId === userProfile.id) {
        // Viewing as self - activeProfileId should be null or own ID
        if (activeProfileId !== targetProfileId && activeProfileId !== null) {
          setActiveProfileId(targetProfileId);
        }
        return;
      }

      // Check if targetProfileId is a valid linked child
      // IMPORTANT: Only validate if linkedKids has actually been loaded (is_parent means they should have kids data)
      // If linkedKids is empty, DON'T clear - the data might not be loaded yet
      const isValidChild = linkedKids.some(k => k.child_id === targetProfileId);

      if (isValidChild) {
        // Valid child - restore if not already set
        if (activeProfileId !== targetProfileId) {
          setActiveProfileId(targetProfileId);
        }
      } else if (linkedKids.length > 0) {
        // Only clear if we have linkedKids data but the child isn't in it
        // This prevents clearing when linkedKids is just empty/loading
        if (activeProfileId) {
          setActiveProfileId(null);
          localStorage.removeItem('activeProfileId');
        }
      }
      // If linkedKids is empty, do nothing - preserve activeProfileId until data loads
    }
  }, [loading, userProfile, linkedKids, activeProfileId]);

  // Switch to viewing as a different profile (for parents)
  const switchProfile = (profileId) => {
    // null = view as self
    // profileId = view as that kid
    setActiveProfileId(profileId);
    // Store in localStorage for persistence
    if (profileId) {
      localStorage.setItem('activeProfileId', profileId);
    } else {
      localStorage.removeItem('activeProfileId');
    }
  };

  // Get the currently active profile data
  // If parent is viewing as kid, return kid's data; otherwise return own profile
  // IMPORTANT: This must be computed BEFORE canTrade/isViewerOnly since they depend on it
  const activeProfile = (() => {
    if (!activeProfileId || !userProfile?.is_parent) {
      return userProfile;
    }

    // Find the kid's profile in linkedKids
    const kidLink = linkedKids.find(k => k.child_id === activeProfileId);
    if (kidLink?.users) {
      return kidLink.users;
    }

    // If activeProfileId doesn't match a child (e.g., it's the parent's own ID),
    // fall back to the parent's own profile
    return userProfile;
  })();

  // Derived: Can the ACTIVE profile trade?
  const canTrade = (() => {
    if (!activeProfile) return false;

    // PRIMARY RULE: Adults ALWAYS have full access, no exceptions
    // This covers: adult non-parents, parents viewing as themselves
    if (!activeProfile.is_under_18) return true;

    // MINOR RULES: Active profile is a minor, check specific permissions

    // Case 1: Parent viewing as child - check that child's family_link permission
    if (activeProfileId && userProfile?.is_parent) {
      const kidLink = linkedKids.find(k => k.child_id === activeProfileId);
      return kidLink?.can_trade === true;
    }

    // Case 2: Minor logged in directly - check if any parent approved trading
    if (linkedParents.length > 0) {
      return linkedParents.some(link => link.can_trade === true);
    }

    // Case 3: Minor with no parents linked cannot trade
    return false;
  })();

  // Derived: Is the ACTIVE profile in viewer-only mode?
  const isViewerOnly = (() => {
    if (!activeProfile) return true; // Not loaded yet, assume restricted

    // PRIMARY RULE: Adults are NEVER viewer-only, no exceptions
    // This covers: adult non-parents, parents viewing as themselves
    if (!activeProfile.is_under_18) return false;

    // MINOR RULES: Active profile is a minor, check specific restrictions

    // Minor who explicitly chose not to trade (wants_to_trade = false)
    if (activeProfile.wants_to_trade === false) return true;

    // Minor who wants to trade but doesn't have permission yet
    return !canTrade;
  })();

  // Refresh profile data (call after updates)
  // Returns Promise so callers can await completion
  const refreshProfile = useCallback(async () => {
    setLoading(true);
    return fetchProfile();
  }, [fetchProfile]);

  const value = {
    // User's own profile
    userProfile,
    // Currently active profile (for parent viewing as kid)
    activeProfile,
    activeProfileId,
    // Family links
    linkedKids,
    linkedParents,
    // Permissions
    canTrade,
    isViewerOnly,
    // Actions
    switchProfile,
    refreshProfile,
    // State
    loading,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
